import React, { useState, useEffect, useCallback } from 'react'
import { Box, Button, Group, TextInput, Select, LoadingOverlay, Text, Flex, Table, ActionIcon, Loader, Divider, Card, ScrollArea } from '@mantine/core'
import { updateCountry, getAllProvinceList, getAllCityList, getProvinceById, getCityById } from '../../../services/location'
import { getGlobalSettings, updateGlobalSetting } from '../../../services/global-settings'
import { getCategoryRules } from '../../../services/category'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconX, IconTrash } from '@tabler/icons-react'
import { useDebouncedCallback } from '@mantine/hooks'

const FormCountry = ({ dataDetail, onCloseModal, reloadList }) => {
    const [loading, setLoading] = useState(false)
    const [form, setForm] = useState({
        name: '',
        currency: '',
        weight_unit: '',
        dimension_unit: '',
        transit_method: '',
        transit_point: '',
        handling_charges: 0,
        deminimis_currency: '',
        shipping_term: '',
        status: 1,
        postcode_required: 0,
        iso_code_2: '',
        iso_code_3: '',
    })

    // Deminimis Levels State
    const [deminimisLevels, setDeminimisLevels] = useState([])
    const [hoveredLevelIndex, setHoveredLevelIndex] = useState(null)

    // Category Rules State
    const [categoryList, setCategoryList] = useState([])
    const [selectedCategory, setSelectedCategory] = useState(null)
    const [excludedCategories, setExcludedCategories] = useState([])
    const [searchCategory, setSearchCategory] = useState('')
    const [categorySearchValue, setCategorySearchValue] = useState('')
    const [loadingCategories, setLoadingCategories] = useState(true)

    useEffect(() => {
        handleFetchCategories('') // Fetch initial categories with an empty query
        // eslint-disable-next-line
    }, []) // Run once on mount



    // Fetch transit point name by ID and type
    const fetchTransitNameById = async (type, id) => {
        try {
            let response
            if (type === 'Province') {
                response = await getProvinceById(id)
            } else if (type === 'City') {
                response = await getCityById(id)
            }
            if (response && response.data) {
                const name = response.data.attributes?.name || response.data.attributes?.description || ''
                return { value: String(response.data.id), label: name }
            }
            return null
        } catch (e) {
            console.error('Error fetching transit name by ID', e)
            return null
        }
    }

    const fetchGlobalSettings = useCallback(async (countryId) => {
        setLoading(true)
        try {
            const params = {
                'filter[country_id][in]': countryId,
                'filter[source_country_id][in]': null,
                'page[limit]': 100
            }
            const response = await getGlobalSettings(params)

            if (response && response.data) {
                const settings = response.data
                let newFormUpdates = {}
                let levels = []
                let excludedCatIds = []
                let transitType = ''
                let transitId = ''

                settings.forEach(item => {
                    const key = item.attributes.key
                    const value = item.attributes.value

                    if (key === 'deminimis_currency') newFormUpdates.deminimis_currency = value
                    if (key === 'shipping_term') newFormUpdates.shipping_term = value
                    if (key === 'handling_charges') newFormUpdates.handling_charges = value

                    if (key === 'deminimis_limit' && value) {
                        try {
                            const parsed = JSON.parse(value)
                            if (Array.isArray(parsed)) {
                                levels = parsed.map(l => ({
                                    limit: l.value || l.limit, // Adjust based on actual API key for 'value' vs 'limit'
                                    duty: l.duty,
                                    wht: l.wht,
                                    vat: l.vat
                                }))
                            }
                        } catch (e) {
                            console.error("Error parsing deminimis_limit", e)
                        }
                    }

                    if (key === 'transit_point' && value) {
                        try {
                            const parsed = JSON.parse(value)
                            if (parsed.type) {
                                transitType = parsed.type.charAt(0).toUpperCase() + parsed.type.slice(1)
                                newFormUpdates.transit_method = transitType
                            }
                            if (parsed.id) {
                                transitId = String(parsed.id)
                                newFormUpdates.transit_point = transitId
                            }
                        } catch (e) {
                            console.error("Error parsing transit_point", e)
                        }
                    }

                    if (key === 'exclude_deminimis_category' && value) {
                        try {
                            const parsed = JSON.parse(value)
                            if (Array.isArray(parsed)) excludedCatIds = parsed
                        } catch (e) {
                            console.error("Error parsing exclude_deminimis_category", e)
                        }
                    }
                })

                setForm(prev => ({ ...prev, ...newFormUpdates }))
                if (levels.length > 0) setDeminimisLevels(levels)
                if (excludedCatIds.length > 0) fetchExcludedCategories(excludedCatIds)

                // Fetch transit name from API and add to transit options
                if (transitType && transitId) {
                    const transitOption = await fetchTransitNameById(transitType, transitId)
                    if (transitOption) {
                        setTransitOptions([transitOption])
                    }
                }
            }

        } catch (error) {
            console.error("Failed to fetch global settings", error)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        console.log("dataDetail", dataDetail)
        if (dataDetail) {
            setForm({
                id: dataDetail.id || '',
                name: dataDetail.name || '',
                currency: dataDetail.currency || '',
                weight_unit: dataDetail.weight_unit || 'kg',
                dimension_unit: dataDetail.dimension_unit || 'cm',
                status: dataDetail.status || 1,
                postcode_required: dataDetail.postcode_required ? 1 : 0,
                iso_code_2: dataDetail.iso_code_2 || '',
                iso_code_3: dataDetail.iso_code_3 || '',
                // Default values that might be overwritten by global settings
                transit_method: '',
                transit_point: '',
                handling_charges: 0,
                deminimis_currency: '',
                shipping_term: '',
            })
            fetchGlobalSettings(dataDetail.id)
        }

    }, [dataDetail, fetchGlobalSettings])

    const fetchExcludedCategories = async (ids) => {
        try {
            // Fetch category details for the IDs found in global settings
            // Assuming getCategoryRules can filter by ID
            const response = await getCategoryRules({
                'filter[id][in]': ids.join(','),
                'page[limit]': ids.length
            })
            if (response && response.data) {
                const formatted = response.data.map(c => ({
                    id: String(c.id), // Ensure ID is string for consistency
                    name: c.attributes.description
                }))
                setExcludedCategories(formatted)
            }
        } catch (e) {
            console.error("Error fetching excluded categories", e)
        }
    }

    // Fetch transit options (province or city) based on selected method and optional search query
    // Fetch transit options (province or city) based on selected method and optional search query
    const fetchTransitOptions = useCallback(async (method, query = '') => {
        try {
            const params = {
                'page[limit]': 20,
                'filter[name][like]': query,
                'filter[country_id][is]': form.id
            }
            let response
            if (method === 'Province') {
                response = await getAllProvinceList(params)
            } else if (method === 'City') {
                response = await getAllCityList(params)
            } else {
                setTransitOptions([])
                return
            }
            if (response && response.data) {
                const formatted = response.data.map(item => ({ value: String(item.id), label: item.attributes.name || item.attributes.description || '' }))
                setTransitOptions(formatted)
            }
        } catch (e) {
            console.error('Error fetching transit options', e)
            setTransitOptions([])
        }
    }, [form.id])

    const handleSearchTransit = useDebouncedCallback(async (query) => {
        if (!form.transit_method) return
        await fetchTransitOptions(form.transit_method, query)
    }, 300)

    // When transit method changes, load initial options (no query)
    // When transit method changes, load initial options (no query)
    useEffect(() => {
        if (form.transit_method) {
            fetchTransitOptions(form.transit_method)
        }
    }, [form.transit_method, fetchTransitOptions])

    const handleFetchCategories = useDebouncedCallback(async (query) => {
        console.log("query", query)
        try {
            setLoadingCategories(true)
            const params = {
                'page[limit]': 20,
                'filter[description][like]': query
            }
            const response = await getCategoryRules(params)
            if (response && response.data) {
                const formatted = response.data.map(c => ({ value: String(c.id), label: c.attributes.description }))
                setCategoryList(formatted)
            }
        } catch (e) {
            console.log("Error fetching categories", e)
        } finally {
            setLoadingCategories(false)
        }
    }, 300)

    const handleChange = (name, val) => {
        setForm((prev) => ({ ...prev, [name]: val }))
    }

    const handleAddDeminimisLevel = () => {
        setDeminimisLevels([...deminimisLevels, { limit: '0', duty: '0', wht: '0', vat: '0' }])
    }

    const handleRemoveDeminimisLevel = (index) => {
        setDeminimisLevels(deminimisLevels.filter((_, i) => i !== index))
    }

    const handleChangeDeminimis = (index, field, val) => {
        const updated = [...deminimisLevels]
        updated[index][field] = val
        setDeminimisLevels(updated)
    }

    const handleAddCategoryRule = () => {
        if (selectedCategory) {
            const cat = categoryList.find(c => c.value === selectedCategory)
            if (cat && !excludedCategories.find(e => e.id === selectedCategory)) {
                setExcludedCategories([...excludedCategories, { id: selectedCategory, name: cat.label }])
                setSelectedCategory(null)
            }
        }
    }

    const handleRemoveCategoryRule = (id) => {
        setExcludedCategories(excludedCategories.filter(c => c.id !== id))
    }

    const handleSubmit = async () => {
        setLoading(true)
        try {
            // Filter out empty or unfilled deminimis levels
            const filledDeminimisLevels = deminimisLevels.filter(level =>
                level.limit || level.duty || level.wht || level.vat
            )
            const payloadMap = [
                "deminimis_currency",
                ...(filledDeminimisLevels.length > 0 ? ["deminimis_limit"] : []),
                "shipping_term",
                "transit_point",
                "exclude_deminimis_category",
                "handling_charges",
            ]

            const payloadGlobalsetting = payloadMap.map(key => {
                return {
                    attributes: {
                        country_id: form.id,
                        source_country_id: null,
                        key: key,
                        value: (key === 'exclude_deminimis_category')
                            ? JSON.stringify(excludedCategories.map(c => c.id))
                            : (key === 'deminimis_limit')
                                ? JSON.stringify(filledDeminimisLevels)
                                : (key === 'transit_point')
                                    ? JSON.stringify({ type: form.transit_method, id: form.transit_point })
                                    : form[key]
                    }
                }
            })

            const payloadCountry = {
                currency: form.currency,
                dimension_unit: form.dimension_unit,
                weight_unit: form.weight_unit,
            }

            const responseGlobalSetting = await updateGlobalSetting({ data: payloadGlobalsetting })
            const response = await updateCountry(dataDetail.id, payloadCountry)
            if (response.success) {
                notifications.show({
                    title: 'Success',
                    message: 'Country updated successfully',
                    color: 'teal',
                    icon: <IconCheck />,
                })
                reloadList()
                onCloseModal()
            } else {
                notifications.show({
                    title: 'Failed',
                    message: response.message || 'Failed to update country',
                    color: 'red',
                    icon: <IconX />,
                })
            }
            if (responseGlobalSetting.success) {
                notifications.show({
                    title: 'Success',
                    message: 'Global setting updated successfully',
                    color: 'teal',
                    icon: <IconCheck />,
                })
            } else {
                notifications.show({
                    title: 'Failed',
                    message: responseGlobalSetting.message || 'Failed to update global setting',
                    color: 'red',
                    icon: <IconX />,
                })
            }
        } catch (error) {
            notifications.show({
                title: 'Error',
                message: error.message || 'Something went wrong',
                color: 'red',
                icon: <IconX />,
            })
        } finally {
            setLoading(false)
        }
    }

    const unitWeightOptions = [
        { label: 'Kg', value: 'kg' },
        { label: 'Lb', value: 'lb' }
    ]

    const unitDimensionOptions = [
        { label: 'cm', value: 'cm' },
        { label: 'm', value: 'm' },
        { label: 'in', value: 'in' }
    ]

    const transitMethodOptions = [
        { label: 'Province', value: 'Province' },
        { label: 'City', value: 'City' }
    ]
    // Options for transit name (province or city) based on selected method
    const [transitOptions, setTransitOptions] = useState([])

    const loadingDataTemplate = () => {
        return (
            <Box ta='center' my={12}>
                <Loader size={16} />
                <Text c='gray.6' fz={11}>Loading Data</Text>
            </Box>
        )
    }

    return (
        <Box pos="relative">
            <LoadingOverlay visible={loading} overlayProps={{ radius: "sm", blur: 2 }} />

            <Box mb="md">
                <Text size="sm" mb="xs">Country Name: <Text span fw={700}>{form.name}</Text></Text>

                {/* Currency */}
                <Text fw={600} size="sm" mt="md" mb={5}>Currency</Text>
                <TextInput
                    label="Currency"
                    value={form.currency}
                    onChange={(e) => handleChange('currency', e.target.value)}
                />

                {/* Unit */}
                <Text fw={600} size="sm" mt="md" mb={5}>Unit</Text>
                <Group grow>
                    <Select
                        label="Default Weight Unit"
                        data={unitWeightOptions}
                        value={form.weight_unit}
                        onChange={(val) => handleChange('weight_unit', val)}
                    />
                    <Select
                        label="Default Dimension Unit"
                        data={unitDimensionOptions}
                        value={form.dimension_unit}
                        onChange={(val) => handleChange('dimension_unit', val)}
                    />
                </Group>

                {/* Transit Point */}
                <Text fw={600} size="sm" mt="md" mb={5}>Transit Point</Text>
                <Group grow>
                    <Select
                        label="Transit Method"
                        data={transitMethodOptions}
                        value={form.transit_method}
                        onChange={(val) => {
                            // Reset transit name when method changes
                            handleChange('transit_method', val)
                            handleChange('transit_point', '')
                            setTransitOptions([])
                        }}
                    />
                    <Select
                        label={form.transit_method ? `Transit Name (${form.transit_method})` : 'Transit Name'}
                        data={transitOptions}
                        placeholder="Select transit point"
                        searchable
                        value={form.transit_point}
                        onSearchChange={(val) => handleSearchTransit(val)}
                        onChange={(val) => handleChange('transit_point', val)}
                        nothingFound="No results"
                    />
                </Group>

                {/* Country Handling Charge */}
                <Text fw={600} size="sm" mt="md" mb={5}>Country Handling Charge</Text>
                <TextInput
                    label="Handling Charge"
                    rightSection={<Text size="xs" c="dimmed">%</Text>}
                    value={form.handling_charges}
                    onChange={(e) => handleChange('handling_charges', e.target.value)}
                />

                {/* Deminimis */}
                <Text fw={600} size="sm" mt="md" mb={5}>Deminimis</Text>
                <Group grow>
                    <TextInput
                        label="Deminimis Currency"
                        value={form.deminimis_currency}
                        onChange={(e) => handleChange('deminimis_currency', e.target.value)}
                    />
                    <TextInput
                        label="Shipping Term"
                        value={form.shipping_term}
                        onChange={(e) => handleChange('shipping_term', e.target.value)}
                    />
                </Group>

                <Group justify="flex-end" mt="sm">
                    <Button color="red" size="xs" onClick={handleAddDeminimisLevel}>Add Deminimis Level</Button>
                </Group>

                {/* Deminimis Levels List */}
                <Box mt="sm">
                    {deminimisLevels.length === 0 ? (
                        <Text size="sm" c="dimmed" ta="center" py="md">No deminimis levels added. Click "Add Deminimis Level" to add one.</Text>
                    ) : (
                        deminimisLevels.map((level, index) => (
                            <Card
                                key={index}
                                shadow="xs"
                                padding="md"
                                radius="md"
                                mb="sm"
                                withBorder
                                onMouseEnter={() => setHoveredLevelIndex(index)}
                                onMouseLeave={() => setHoveredLevelIndex(null)}
                                style={{ position: 'relative' }}
                            >
                                <Group justify="space-between" mb="xs">
                                    <Text size="sm" fw={600} c="dimmed">Level {index + 1}</Text>
                                    <ActionIcon
                                        color="red"
                                        variant="subtle"
                                        size="sm"
                                        onClick={() => handleRemoveDeminimisLevel(index)}
                                        style={{
                                            opacity: hoveredLevelIndex === index ? 1 : 0,
                                            transition: 'opacity 0.2s ease'
                                        }}
                                    >
                                        <IconTrash size={16} />
                                    </ActionIcon>
                                </Group>
                                <Group grow align="flex-end">
                                    <TextInput
                                        label="Deminimis Limit"
                                        leftSection={<Text size="xs" c="dimmed">{form.deminimis_currency}</Text>}
                                        value={level.limit}
                                        onChange={(e) => handleChangeDeminimis(index, 'limit', e.target.value)}
                                    />
                                    <TextInput
                                        label="Deminimis Limit Duty"
                                        rightSection={<Text size="xs" c="dimmed">%</Text>}
                                        value={level.duty}
                                        onChange={(e) => handleChangeDeminimis(index, 'duty', e.target.value)}
                                    />
                                    <TextInput
                                        label="Deminimis Limit WHT"
                                        rightSection={<Text size="xs" c="dimmed">%</Text>}
                                        value={level.wht}
                                        onChange={(e) => handleChangeDeminimis(index, 'wht', e.target.value)}
                                    />
                                    <TextInput
                                        label="Deminimis Limit VAT"
                                        rightSection={<Text size="xs" c="dimmed">%</Text>}
                                        value={level.vat}
                                        onChange={(e) => handleChangeDeminimis(index, 'vat', e.target.value)}
                                    />
                                </Group>
                            </Card>
                        ))
                    )}
                </Box>

                {/* Add Deminimis Category Rules Excludes */}
                <Text fw={600} size="sm" mt="md" mb={5}>Add Deminimis Category Rules Excludes</Text>
                <Select
                    label="Category Rules"
                    placeholder="Select category"
                    data={categoryList}
                    searchable
                    onSearchChange={(val) => {
                        setCategorySearchValue(val)
                        handleFetchCategories(val)
                    }}
                    searchValue={categorySearchValue}
                    value={selectedCategory}
                    onChange={(val) => {
                        setSelectedCategory(val)
                    }}
                    mb="xs"
                    clearable
                    nothingFoundMessage={!loadingCategories ? categoryList.length > 0 ? '' : 'No Category Found' : loadingDataTemplate()}
                />
                <Button variant="default" size="xs" onClick={handleAddCategoryRule}>Add Category Rules</Button>

                {/* Selected Deminimis Category Rules Excludes */}
                <Text fw={600} size="sm" mt="lg" mb={5}>Selected Deminimis Category Rules Excludes</Text>
                <Group justify="space-between" mb="sm">
                    <Group>
                        <Button variant="default" size="xs">Select all</Button>
                        <Button variant="default" size="xs">Clear all</Button>
                    </Group>
                    <TextInput
                        placeholder="Search Category"
                        size="xs"
                        value={searchCategory}
                        onChange={(e) => setSearchCategory(e.target.value)}
                    />
                </Group>

                <ScrollArea h={200} type="auto" offsetScrollbars>
                    <Table withTableBorder withColumnBorders>
                        <Table.Thead bg="gray.1">
                            <Table.Tr>
                                <Table.Th>Description</Table.Th>
                                <Table.Th w={150}>Action</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {excludedCategories.length > 0 ? excludedCategories.map((cat) => (
                                <Table.Tr key={cat.id}>
                                    <Table.Td>{cat.name}</Table.Td>
                                    <Table.Td>
                                        <Button color="red" size="compact-xs" onClick={() => handleRemoveCategoryRule(cat.id)}>Remove from bulk</Button>
                                    </Table.Td>
                                </Table.Tr>
                            )) : (
                                <Table.Tr>
                                    <Table.Td colSpan={2} align="center"><Text size="sm" c="dimmed">No categories excluded</Text></Table.Td>
                                </Table.Tr>
                            )}
                        </Table.Tbody>
                    </Table>
                </ScrollArea>

            </Box>

            <Divider my="lg" />

            <Flex justify="flex-end" gap="sm">
                <Button variant="default" onClick={onCloseModal}>Cancel</Button>
                <Button color="red" onClick={handleSubmit}>Save now</Button>
            </Flex>
        </Box>
    )
}

export default FormCountry
